// Every title a trainer can wear, in one order, with how it was earned. Titles are kept as plain strings (on
// trophies and in pass.titles), so where one came from is read back from its text: tournament places, ranked
// seasons, the monthly podium, the Weekly Catch, boss crests and season passes. The player screen's Card panel
// (client/player-hub.ts) lists them in this order, and each one's tip says how it was earned.
import { TOUR_TIERS, titleFor, type TourTier, type Trophy } from './tournament.ts';
import { RANKED_ART } from './ranked.ts';
import { PODIUM, PODIUM_BOARDS } from './leaderboards.ts';
import { WEEK_CATCH } from './weekcatch.ts';
import { CREST_TITLE } from './items.ts';
import { SEASONS } from './pass.ts';
import type { TrainerDTO } from './protocol.ts';

const TIERS: TourTier[] = ['grand', 'rising', 'weekly'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PLACE = ['1st', '2nd', '3rd'];
const BOSS: Record<keyof typeof CREST_TITLE, string> = { fenrir: 'Fenrir', typhon: 'Typhon', hachimata: 'Hachimata' };

export interface TitleInfo {
  /** How it was earned, one short sentence. */
  how: string;
  /** Sort key, compared item by item, lower first. */
  key: number[];
}

/** Where a title came from and where it sorts: the biggest wins first, newest first within a kind. */
export function titleInfo(title: string, trophies: readonly Trophy[] = []): TitleInfo {
  for (const [ti, tier] of TIERS.entries()) {
    for (const place of [1, 2] as const) {
      if (title !== titleFor(tier, place)) continue;
      const n = trophies.filter((x) => x.tier === tier && x.place === place).length;
      const name = TOUR_TIERS[tier].name;
      const times = n > 1 ? ` ${n} times` : '';
      return { how: place === 1 ? `Won the ${name}${times}.` : `Reached the ${name} final${times}.`, key: [0, ti, place] };
    }
  }
  for (const a of RANKED_ART) {
    if (title === a.legendTitle) return { how: `Finished ranked season ${a.no} in Legend.`, key: [1, -a.no, 0] };
    if (title === a.title) return { how: `Reached Silver league in ranked season ${a.no}.`, key: [1, -a.no, 1] };
  }
  const m = /^(\w+) · (.+) · (\w{3}) (\d{4})$/.exec(title);
  const medal = m ? (PODIUM.medals as readonly string[]).indexOf(m[1]) : -1;
  const board = m ? PODIUM_BOARDS.find((b) => PODIUM.label[b] === m[2]) : undefined;
  const mon = m ? MONTHS.indexOf(m[3]) : -1;
  if (m && medal >= 0 && board && mon >= 0) {
    const when = `${m[3]} ${m[4]}`;
    const how = board === 'guild' ? `Your guild finished ${PLACE[medal]} on the Guild board in ${when}.` : `Finished ${PLACE[medal]} on the ${m[2]} board in ${when}.`;
    return { how, key: [2, -(Number(m[4]) * 12 + mon), medal, PODIUM_BOARDS.indexOf(board)] };
  }
  if (title === WEEK_CATCH.champion) return { how: 'Finished 1st on the Weekly Catch board.', key: [3, 0] };
  if (title === WEEK_CATCH.podium) return { how: `Finished top ${WEEK_CATCH.podiumTo} on the Weekly Catch board.`, key: [3, 1] };
  for (const [i, [b, t]] of Object.entries(CREST_TITLE).entries()) {
    if (title === t) return { how: `Crafted the ${BOSS[b as keyof typeof CREST_TITLE]} Crest.`, key: [4, i] };
  }
  for (const s of SEASONS) {
    const pass = `the ${s.name} pass`;
    if (title === s.plus.title) return { how: `Bought ${s.name} Pass+.`, key: [5, -s.no, 0] };
    for (const [n, x] of s.tiers.entries()) {
      if (x.prem.some((r) => r.k === 'title' && r.id === title)) return { how: `Premium tier ${n + 1} of ${pass}.`, key: [5, -s.no, 1, -n] };
      if (x.free.some((r) => r.k === 'title' && r.id === title)) return { how: `Free tier ${n + 1} of ${pass}.`, key: [5, -s.no, 1, -n] };
    }
  }
  return { how: '', key: [9] };
}

const cmp = (a: number[], b: number[]) => {
  for (let i = 0; i < Math.max(a.length, b.length); i++) if ((a[i] ?? 0) !== (b[i] ?? 0)) return (a[i] ?? 0) - (b[i] ?? 0);
  return 0;
};

/** Every title this trainer may wear (tournament finals and kept titles), sorted, each with how it was earned. */
export function wearableTitles(t: Pick<TrainerDTO, 'trophies' | 'pass'>): { title: string; how: string }[] {
  const trophies = t.trophies ?? [];
  const all = [...new Set([...trophies.filter((x) => x.place <= 2).map((x) => titleFor(x.tier, x.place as 1 | 2)), ...(t.pass?.titles ?? [])])];
  return all.map((title) => ({ title, ...titleInfo(title, trophies) }))
    .sort((a, b) => cmp(a.key, b.key) || a.title.localeCompare(b.title))
    .map(({ title, how }) => ({ title, how }));
}
